/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import type { SetProps } from './types'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { get } from '../../../utils/object'
import { getFlex, omitFlex } from '../../../utils/props-utils'
import { FlexPrivate } from '../../_primitives/flex/flex'

/**
 * @deprecated please switch to use Flex with props gap and flex-direction instead
 */
const Set: FC<SetProps> = React.forwardRef<
  HTMLDivElement,
  SetProps & {
    theme?: Theme
  }
>(function Set(props, ref) {
  const {
    className,
    spacing: spacingProps,
    vertical,
    filled,
    children,
    noWrap,
    skipChild = [],
    useCloneSpacingStrategyOnly,
    ...rest
  } = props
  const theme = useSafeThemeUI(props)
  const flexProps = getFlex(props)
  const spacing =
    typeof spacingProps === 'string' && spacingProps.startsWith('sizes')
      ? get(theme, spacingProps)
      : spacingProps
  // css children selector is unreliable because it is unpredictable the
  // order of css generated by emotion we have to resort to appending
  // the css props that we need directly to the children props.
  // However, clone strategy may not work in some scenario, like when
  // the component is wrapped with a controller, so having css children
  // selector there is still useful.  We can opt out of this by using
  // useCloneSpacingStrategyOnly prop.
  const childrenArray = React.Children.toArray(children)

  const { marginBottom, marginLeftTop, negativeMarginLeftTop } = useMemo(() => {
    const negativeSpacing = Array.isArray(spacing)
      ? spacing.map(i => `-${i}`)
      : `-${spacing}`
    return {
      marginBottom: {
        mb: spacing
      },
      marginLeftTop: {
        ml: spacing,
        mt: spacing
      },
      negativeMarginLeftTop: {
        ml: negativeSpacing,
        mt: negativeSpacing
      }
    }
  }, [spacing])

  const _children = childrenArray.map(function (child: any, i: number) {
    // eslint-disable-next-line no-console
    // console.log(
    //   `Set cloning margin to ${child?.type?.__docgenInfo?.displayName}`
    // )
    return !skipChild.includes(i)
      ? React.cloneElement(child, {
          ...child.props,
          ...(vertical && i < childrenArray.length
            ? i < childrenArray.length - 1
              ? marginBottom
              : {}
            : marginLeftTop)
        })
      : child
  })
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      '[<Set/> is a deprecated] --- please switch to use Flex with props gap and flex-direction instead'
    )
  }
  return (
    <FlexPrivate
      ref={ref}
      className={classNames(
        {
          'csg-tui-set': true
        },
        className
      )}
      {...omitFlex(rest)}
      __css={
        vertical
          ? {
              flexDirection: 'column',
              ...(filled ? { width: '100%' } : { alignItems: 'flex-start' }),
              ...flexProps,
              ...(useCloneSpacingStrategyOnly
                ? {}
                : {
                    '& > *': {
                      '&:not(:last-child)': marginBottom,
                      ...skipChild.reduce(
                        (acc, cur) => ({
                          ...acc,
                          [`&:not(:nth-child(${cur}))`]: {
                            marginBottom: 0
                          }
                        }),
                        {}
                      )
                    }
                  })
            }
          : {
              flexDirection: 'row',
              ...(noWrap ? {} : { flexWrap: 'wrap' }),
              alignItems: 'center',
              justifyContent: 'flex-start',
              ...flexProps,
              ...negativeMarginLeftTop,
              ...(useCloneSpacingStrategyOnly
                ? {}
                : {
                    '& > *': marginLeftTop
                  })
            }
      }
    >
      {_children}
    </FlexPrivate>
  )
})

Set.defaultProps = {
  spacing: 'sm'
}

/**
 * @deprecated use <Flex> with gap props is better.
 */
export default Set
